import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
Política de Privacidad

Última actualización: febrero 2026

Tu privacidad es importante para nosotros. Esta política describe cómo recopilamos, usamos y protegemos tu información personal.

Información que recopilamos
• Información de compra: nombre, correo electrónico y datos de pago necesarios para procesar tu pedido.
• Información de uso: datos anónimos sobre cómo interactúas con nuestro sitio para mejorar la experiencia.

Cómo usamos tu información
• Para procesar y entregar tu compra.
• Para enviarte actualizaciones relacionadas con tu producto (solo si das tu consentimiento).
• Para mejorar nuestros productos y servicios.

Protección de datos
Utilizamos medidas de seguridad estándar de la industria para proteger tu información personal. No vendemos, alquilamos ni compartimos tu información personal con terceros con fines de marketing.

Tus derechos
Tienes derecho a:
• Acceder a tus datos personales.
• Solicitar la corrección o eliminación de tus datos.
• Retirar tu consentimiento en cualquier momento.

Cookies
Nuestro sitio puede utilizar cookies para mejorar tu experiencia de navegación. Puedes configurar tu navegador para rechazar cookies.

Cambios a esta política
Nos reservamos el derecho de actualizar esta política. Cualquier cambio será publicado en esta página.
`.trim();
const termsContent = `
Términos y Condiciones

Última actualización: febrero 2026

Al comprar y utilizar "Reset de Deudas en 14 Días (USA)", aceptas los siguientes términos y condiciones.

Producto digital
Este es un producto digital (PDF) que se entrega de forma inmediata tras la compra. No se ofrece asesoría financiera, legal ni fiscal personalizada.

Uso personal
El contenido es para uso personal únicamente. Queda prohibida la reproducción, distribución o reventa del material sin autorización expresa.

Resultados
Los resultados pueden variar según la situación financiera individual de cada persona. No garantizamos resultados específicos. El producto ofrece herramientas y estrategias educativas.

Política de reembolso
Debido a la naturaleza digital del producto y su entrega inmediata, todas las ventas son finales. Si tienes algún problema con tu compra, contáctanos y haremos lo posible por ayudarte.

Propiedad intelectual
Todo el contenido, diseño y materiales incluidos en este producto están protegidos por derechos de autor.

Limitación de responsabilidad
Este producto tiene fines educativos. No somos responsables de decisiones financieras tomadas basándose en el contenido del producto. Consulta con un profesional financiero para asesoría personalizada.

Modificaciones
Nos reservamos el derecho de modificar estos términos en cualquier momento. Los cambios entrarán en vigor inmediatamente después de su publicación.
`.trim();
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const modalContent = activeModal === 'privacy' ? privacyContent : termsContent;
  return <>
      <footer className="w-full py-8 px-6" style={{
      backgroundColor: 'var(--navy)'
    }} data-id="element-401">
        <div className="max-w-2xl mx-auto flex flex-col items-center gap-4" data-id="element-402">
          <div className="flex items-center gap-6" data-id="element-403">
            <button onClick={() => setActiveModal('privacy')} className="text-sm underline underline-offset-2 cursor-pointer transition-opacity duration-200 hover:opacity-100" style={{
            color: 'var(--gold)',
            opacity: 0.7
          }} data-id="element-404">
              Política de Privacidad
            </button>
            <span style={{
            color: 'var(--gold)',
            opacity: 0.3
          }} data-id="element-405">
              |
            </span>
            <button onClick={() => setActiveModal('terms')} className="text-sm underline underline-offset-2 cursor-pointer transition-opacity duration-200 hover:opacity-100" style={{
            color: 'var(--gold)',
            opacity: 0.7
          }} data-id="element-406">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-xs" style={{
          color: 'var(--gold)',
          opacity: 0.35
        }} data-id="element-407">
            © {new Date().getFullYear()} Reset de Deudas en 14 Días. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>

      {/* Modal / Popup */}
      <AnimatePresence data-id="element-408">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{
        backgroundColor: 'rgba(27, 42, 74, 0.6)',
        backdropFilter: 'blur(4px)'
      }} onClick={() => setActiveModal(null)} data-id="element-409">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="w-full max-w-lg max-h-[80vh] rounded-2xl overflow-hidden flex flex-col" style={{
          backgroundColor: 'var(--cream)',
          border: '1px solid rgba(200, 164, 92, 0.2)',
          boxShadow: '0 25px 50px -12px rgba(27, 42, 74, 0.3)'
        }} onClick={e => e.stopPropagation()} data-id="element-410">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{
            borderBottom: '1px solid rgba(27, 42, 74, 0.08)'
          }} data-id="element-411">
                <h3 className="font-serif text-lg font-semibold" style={{
              color: 'var(--navy)'
            }} data-id="element-412">
                  {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h3>
                <button onClick={() => setActiveModal(null)} className="p-1.5 rounded-lg cursor-pointer transition-colors duration-200" style={{
              color: 'var(--navy)'
            }} aria-label="Cerrar" data-id="element-413">
                  <XIcon className="w-5 h-5" data-id="element-414" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="overflow-y-auto px-6 py-5 flex-1" data-id="element-415">
                {modalContent.split('\n\n').map((paragraph, i) => {
              const isTitle = i === 0;
              const isSubheading = paragraph === paragraph.trim() && !paragraph.startsWith('•') && paragraph.length < 60 && i > 0 && !paragraph.startsWith('Última');
              if (isTitle) return null; // Already shown in header
              if (isSubheading && !paragraph.startsWith('Última')) {
                return <h4 key={i} className="font-semibold text-sm mt-6 mb-2" style={{
                  color: 'var(--navy)'
                }} data-id="element-416">
                        {paragraph}
                      </h4>;
              }
              return <p key={i} className="text-sm mb-3 whitespace-pre-line" style={{
                color: 'var(--charcoal)',
                lineHeight: '1.7',
                opacity: 0.85
              }} data-id="element-417">
                      {paragraph}
                    </p>;
            })}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}